import { type ReactNode, createContext, useCallback, useContext } from "react";

export interface EntityRef {
	entityType: string;
	entityId: string;
	displayText: string;
}

const MIME = "application/x-omnios-entity";

// ── Drag helpers ──

export function setEntityDragData(e: React.DragEvent, entity: EntityRef) {
	e.dataTransfer.setData(MIME, JSON.stringify(entity));
	e.dataTransfer.effectAllowed = "copy";
}

export function getEntityDropData(e: React.DragEvent): EntityRef | null {
	const raw = e.dataTransfer.getData(MIME);
	if (!raw) return null;
	try {
		return JSON.parse(raw) as EntityRef;
	} catch {
		return null;
	}
}

export function hasEntityDrag(e: React.DragEvent): boolean {
	return e.dataTransfer.types.includes(MIME);
}

// ── Context for injecting entities into agent ──

interface EntityContextValue {
	addEntityContext: (entity: EntityRef) => void;
	removeEntityContext: (entityType: string, entityId: string) => void;
	entities: EntityRef[];
}

const EntityContext = createContext<EntityContextValue>({
	addEntityContext: () => {},
	removeEntityContext: () => {},
	entities: [],
});

export const useEntityContext = () => useContext(EntityContext);

export function EntityContextProvider({
	value,
	children,
}: {
	value: EntityContextValue;
	children: ReactNode;
}) {
	return (
		<EntityContext.Provider value={value}>{children}</EntityContext.Provider>
	);
}

// ── Draggable wrapper ──

export function DraggableEntity({
	entity,
	children,
	className,
}: {
	entity: EntityRef;
	children: ReactNode;
	className?: string;
}) {
	const onDragStart = useCallback(
		(e: React.DragEvent) => {
			setEntityDragData(e, entity);
		},
		[entity],
	);

	return (
		<span
			draggable
			onDragStart={onDragStart}
			className={className}
			style={{ cursor: "grab" }}
		>
			{children}
		</span>
	);
}
